import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { StatusBadge } from "../shared/StatusBadge";
import { deliveryStatusMeta } from "../../lib/statusMaps";
import { podImageUrl, formatDate } from "../../lib/utils";

function PodImage({ filename }) {
  return (
    <div className="aspect-[3/4] w-full overflow-hidden rounded-md border border-border bg-background">
      <img
        src={podImageUrl(filename)}
        alt="Proof of delivery"
        className="h-full w-full object-contain"
        data-testid="delivery-detail-pod-image"
        onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
      />
      <div className="hidden h-full w-full items-center justify-center text-xs text-text-muted">Image not available</div>
    </div>
  );
}

// Popup showing one day's delivery details — date, delivery time, status, and
// both proofs of delivery. Used from the calendar (day click) and the list
// view (Proof of Delivery button).
export function DeliveryDetailDialog({ open, onOpenChange, delivery, date, statusLabel }) {
  const images = delivery ? [delivery.pof1, delivery.pof2].filter(Boolean) : [];
  const meta = delivery && delivery.delivery_status != null ? deliveryStatusMeta(delivery.delivery_status) : null;
  const theDate = date || delivery?.date;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delivery-detail-dialog">
        <DialogHeader>
          <DialogTitle>Delivery — {formatDate(theDate)}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3 rounded-md border border-border bg-background p-3 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-text-muted">Date</p>
              <p className="mt-1 font-medium text-text-main" data-testid="delivery-detail-date">{formatDate(theDate)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-text-muted">Status</p>
              <div className="mt-1" data-testid="delivery-detail-status">
                {delivery?.delivery_status === 2 && delivery?.delivered_by_admin
                  ? <StatusBadge label="Delivered by Admin" variant="success" data-testid="delivery-detail-admin-delivered" />
                  : meta ? <StatusBadge label={meta.label} variant={meta.variant} /> : <span className="text-text-main">{statusLabel || "—"}</span>}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-text-muted">Delivery time</p>
              <p className="mt-1 font-medium text-text-main" data-testid="delivery-detail-time">
                {delivery?.delivery_status === 2 ? (delivery?.arrival || "—") : "—"}
              </p>
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Proof of Delivery</p>
            {images.length > 0 ? (
              <div className="grid grid-cols-2 gap-3" data-testid="delivery-detail-pods">
                {images.map((f) => <PodImage key={f} filename={f} />)}
              </div>
            ) : (
              <p className="py-4 text-center text-sm text-text-muted" data-testid="delivery-detail-no-pod">No proof of delivery on file.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
